import { Icon } from "./Icon";
import type { IconName } from "./icons/paths";

/**
 * Группа табов. В пакете дизайна их четыре — режим и период рейтинга, период
 * заработка, фильтр активности — и все четыре собраны из одного `.segmented`,
 * но выглядят двумя разными способами.
 *
 * `outline` — активный пункт получает внутреннюю рамку с зелёным свечением и
 * зелёный текст, между пунктами стоят волосяные разделители. Так сделаны
 * рейтинг и активность: там таб — это фильтр, он не должен кричать.
 *
 * `solid` — активный пункт залит акцентом, текст на нём тёмный, разделителей
 * нет. Так сделан период заработка: там таб управляет главной цифрой экрана.
 *
 * `aria-pressed` и `data-haptic="select"` — как у прежних `ActivityFilter` и
 * `MarketFilter`: тесты ходят по доступным именам и нажатому состоянию, а
 * отклик на касание раздаёт делегат (FE-14), не обработчик.
 */
export type SegmentedVariant = "outline" | "solid";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: IconName;
}

interface SegmentedProps<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  ariaLabel: string;
  variant?: SegmentedVariant;
  className?: string;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  variant = "outline",
  className,
}: SegmentedProps<T>) {
  const solid = variant === "solid";

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={[
        "flex items-stretch overflow-hidden rounded-md border border-border-subtle",
        "bg-[image:var(--surface-control)]",
        solid ? "p-0.5" : "",
        className ?? "",
      ].join(" ")}
    >
      {options.map((option, index) => {
        const active = value === option.value;

        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            data-haptic="select"
            onClick={() => {
              onChange(option.value);
            }}
            className={[
              "press relative flex flex-1 items-center justify-center gap-1.5 px-2 py-2.5",
              "text-small font-medium",
              "transition-colors duration-[var(--motion-fast)] ease-standard",
              solid ? segmentSolid(active) : segmentOutline(active),
              // Волосяной разделитель слева у каждого пункта кроме первого —
              // только в outline: у залитого варианта он резал бы пилюлю.
              !solid && index > 0
                ? "before:absolute before:top-[22%] before:left-0 before:h-[56%] before:w-px before:bg-border-hair before:content-['']"
                : "",
            ].join(" ")}
          >
            {option.icon ? <Icon name={option.icon} size={14} /> : null}
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function segmentOutline(active: boolean): string {
  if (!active) return "text-text-secondary";
  // Рамка активного пункта — не border самой кнопки: она отступает внутрь на
  // 3px, чтобы не сливаться с границей контейнера (`.segment-btn.active::after`).
  return [
    "text-status-profit",
    "after:absolute after:inset-[3px] after:rounded-sm after:border after:border-status-profit/20",
    "after:bg-surface-selected after:content-['']",
    // Рамка лежит ПОД подписью: в макете это `z-index:-1`, здесь — порядок
    // слоёв, потому что отрицательный z-index вывалил бы её за фон кнопки.
    "after:-z-1",
  ].join(" ");
}

function segmentSolid(active: boolean): string {
  if (!active) return "rounded-sm text-text-secondary";
  return "rounded-sm bg-[image:var(--surface-accent)] font-bold text-text-inverse";
}
